import { Code, ConnectError } from '@connectrpc/connect'
import type { ErrorEvent, EventHint, init as sentryInit } from '@sentry/react'

import { parseStructuredError } from '@modules/error/errorCodes'

/**
 * 個人情報になりうるヘッダー名・クエリ名のパターン（部分一致）。
 *
 * SDK v10 以前が PII スクラブとして内部で使っていた除外リストと同じもの。
 * IP アドレス（`x-forwarded-for` / `x-real-ip` / `remote-addr`）・経路情報（`via`）・
 * 利用者情報（`x-user-id` 等）が Sentry に載らないようにする。
 *
 * 現在のブラウザ SDK が集めるヘッダーは Referer と User-Agent だけで、このリストはどちらにも
 * 当たらない。`httpClientIntegration` のようにヘッダーを送る計測を将来入れたときの備えとして置く。
 */
const SENTRY_PII_KEY_DENY_LIST = ['forwarded', '-ip', 'remote-', 'via', '-user']

/**
 * `Sentry.init` の `dataCollection` の型。
 *
 * SDK が `DataCollection` 型を `@sentry/react` から再エクスポートしていないため、
 * `init` のオプション型から取り出す（`@sentry/core` への直接依存を増やさないため）。
 */
type SentryDataCollection = NonNullable<Parameters<typeof sentryInit>[0]>['dataCollection']

/**
 * Sentry.init の dataCollection。company / admin / customer の 3 アプリで共通。
 * （company のアップロードキューの worker は自分では init せず、company の client に相乗りする）
 *
 * SDK v11 で `sendDefaultPii` が `dataCollection` に置き換わり、**未設定だと収集する側が既定**に
 * なった（v9 / v10 は未設定 = 収集しない）。本プロジェクトは SDK に個人情報を自動収集させない方針なので、
 * 既定に任せず明示する。ここを緩めると施主画面（customer）を含む 3 アプリで PII が Sentry に入る。
 *
 * 値は更新前（9.46）より緩めないように揃えてある。ブラウザで意味を持たないカテゴリ
 * （DB クエリ・キュー・GraphQL・GenAI）は、該当する計測を入れていないため指定しない。
 */
export const SENTRY_DATA_COLLECTION: SentryDataCollection = {
  // 利用者情報（IP アドレス等）を SDK に自動収集させない。利用者の識別は setUser で明示的に渡す
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { deny: SENTRY_PII_KEY_DENY_LIST },
    response: { deny: SENTRY_PII_KEY_DENY_LIST },
  },
  // リクエスト / レスポンスの本文は送らない（v11 の既定は全種類を送る）
  httpBodies: [],
  urlQueryParams: { deny: SENTRY_PII_KEY_DENY_LIST },
}

/** Sentry ignoreErrors に設定するサードパーティ起因のエラーパターン */
export const SENTRY_IGNORE_ERRORS: string[] = [
  // Microsoft Clarity の内部エラー（サードパーティ）
  't.replace is not a function',
  // Firebase Auth のユーザーセッション失効系（別タブログアウト・refresh token 失効など）
  // SDK 内部の unhandledrejection としてのみ届くため、アプリのバグではない
  'Firebase: Error (auth/user-token-expired)',
  'Firebase: Error (auth/user-disabled)',
  'Firebase: Error (auth/user-not-found)',
  // 画面遷移・リロード時に進行中の fetch が AbortController.abort() で中断されるケース。
  // unhandledrejection としてのみ届き stacktrace も無いため、アプリのバグではなくノイズ。
  'AbortError',
  // iOS Safari / Chrome Mobile iOS の IndexedDB が Blob/File 保存時にスローする WebKit 起因のエラー。
  // stacktrace 無し・mechanism=onunhandledrejection で届き、アプリ側で防ぐ手段が無い。
  'Error preparing Blob/File data to be stored in object store',
  // Safari の IndexedDB セッションがブラウザ都合で切断されたときのエラー。
  // stacktrace 無し・onunhandledrejection で届くノイズ。ページ再読込で復旧可能。
  'Connection to Indexed Database server lost',
  // iOS Safari がタブサスペンド時に IndexedDB トランザクションを abort した後、
  // uploadQueue の delete 操作が reject する WebKit 起因のエラー。
  // stacktrace 無し・onunhandledrejection で届き、uploaded 済みジョブの掃除は
  // 次回セッションの initWorker で回収されるため実害なし。
  'Attempt to delete range from database without an in-progress transaction',
  // 上記 delete range と同一 root cause（await 跨ぎで tx が auto-commit された後の read/write）。
  // read 側（store.index().getAll()）が非アクティブ tx で失敗すると get all index records として届く。
  // queue.ts は read/write の tx を分離して恒久修正済み（issue #1795）だが、旧デプロイ済みバンドルが
  // prod に伝播しきるまでのノイズ抑制として登録。prod 沈黙確認後に両エントリを掃除予定（umbrella #1933）。
  'Attempt to get all index records from database without an in-progress transaction',
  // vaul (Drawer ライブラリ) が Mobile Safari の pointerout で event.target=null を踏むサードパーティバグ。
  // node_modules/vaul/dist/index.mjs:1189 のみで発生し、アプリ側で予防できない。
  "null is not an object (evaluating 'e.target')",
  // react-gtm-module が挿入する gtm.js への到達失敗 (アドブロック / ネットワーク blip / 企業プロキシ)。
  // stacktrace はアプリコード無し・@sentry/core の fetch wrapper のみで、アプリ側で予防できない。
  'Failed to fetch (www.googletagmanager.com)',
  // セキュリティ製品 (Web フィルタリング / プロキシ型) がブロックページ由来の拒否応答を返すときの
  // メッセージ (IESAPURI-COMPANY-9W)。stacktrace 無し・onunhandledrejection のみで届き、
  // breadcrumb もサードパーティ (clarity.ms / cdn-cgi/rum) だけでアプリコードに到達していない。
  // "Reference number: <hex>" はイベントごとに変わるため前半の固定文言でマッチさせる。
  "You don't have permissions. Reference number:",
  // iOS Safari の Quick Look / Web プレビューが注入する Apple 独自 CSP
  // (`default-src x-apple-ql-id: 'unsafe-inline'`) が wasm-unsafe-eval を許可しないため、
  // react-pdf (yoga-layout) の WASM compile が失敗するケース (issue #2072 / IESAPURI-COMPANY-9N)。
  // WorkerのCSPは観測のみ（Report-Only）でブロックしないので、このエラーの原因にはならない。
  // ブラウザプレビュー文脈起因でアプリ側で予防できない。
  'Refused to create a WebAssembly object',
]

/** Sentry denyUrls に設定するサードパーティスクリプトのURLパターン */
export const SENTRY_DENY_URLS: RegExp[] = [
  // Microsoft Clarity
  /clarity\.js/i,
]

/**
 * 「想定内のユーザー導線で必ず出るアプリエラー」のコード。
 *
 * サーバーが返す構造化エラーコード（google.rpc.ErrorInfo の reason）で判定する。
 * ここに載せたものは Sentry へ送らない。ユーザーにはトーストでちゃんと日本語メッセージが出るため、
 * 「エラーを握り潰す」のではなく「バグではない事象を Sentry の監視対象から外す」という意図。
 *
 * 追加するときの基準: そのコードが返る経路が **アプリの不具合ではなく、ユーザーの操作や
 * リンクの状態から必然的に生じる** と言い切れること。判断が付かないものは載せない。
 */
export const SENTRY_EXPECTED_APP_ERROR_CODES: string[] = [
  // 設定完了後の再送信・古いリンク・二重送信で必ず出る
  // （issue #2646 / IESAPURI-COMPANY-6E / B7。/login 側の 69 は別経路なのでここには載せない）
  'PASSWORD_RESET_LINK_INVALID',
  // AI 再実行の回数上限（マスクの作り直し / 図面の解析やり直し）。上限は運用値で、
  // 到達したら画面が実行ボタンを止めて日本語で案内する業務上の想定内の状態
  // （design-5.5-regeneration-limits.md。CS 介入の起点はサーバー側の Warn ログ）。
  // Connect code は failed_precondition なので `invalid_argument` の除外には掛からず、
  // Sentry の ERROR として Slack の障害通知まで流れていた。
  'MASK_REGENERATION_LIMIT_EXCEEDED',
  'AREA_REANALYZE_LIMIT_EXCEEDED',
  // プラン申し込みダイアログの連投ガード（同一スタッフ1分1回）。送信ボタンの二度押しで
  // 必ず出るうえ、画面にはサーバーの日本語メッセージがトーストで出る
  // （Connect codeはresource_exhaustedなのでinvalid_argumentの除外には掛からない）
  'PLAN_INQUIRY_RATE_LIMITED',
  // クレジット残高不足。契約中でも残高が尽きれば必ず出る業務上の想定内の状態で、
  // 画面には残高ブロック・バナー・日本語のエラー文言が出る
  // （Connect codeはfailed_preconditionなのでinvalid_argumentの除外には掛からない）
  'INSUFFICIENT_CREDIT',
  // 停止済みスタッフの認証拒否。企業が停止したアカウントで必ず出る業務上の想定内の状態で、
  // ログイン画面に理由のバナーが出る。`getStaffMe` の失敗はfatalかどうかに関係なく
  // captureExceptionする（原因を追える形にしておくため）ので、ここで抑制する
  // （Connect codeはunauthenticatedなのでinvalid_argumentの除外には掛からない）
  'STAFF_ACCOUNT_DISABLED',
  // サポートアカウント（ハウスケープの社員が使う）のログイン期限の外での認証拒否（決定125）。
  // 普段はOFFが既定なので、OFFのまま開いた端末から必ず出る想定内の状態
  'SUPPORT_ACCESS_DISABLED',
  // トークンの期限切れ・失効。パスワード変更・管理者による失効・長時間放置で必ず出る。
  // 画面は再ログインを促す文言を出す
  'SESSION_EXPIRED',
]

/**
 * Sentry.init の beforeSend。company / admin / customer で共通の抑制ルールを適用する。
 *
 * 1. `invalid_argument` … ユーザー入力の不正であり、アプリのバグではない
 * 2. SENTRY_EXPECTED_APP_ERROR_CODES … 想定内の導線で必ず出るアプリエラー
 * 3. onunhandledrejectionで届いたIndexedDBの接続切断（isUnhandledIdbConnectionClosing）
 *
 * `not_found` を一律に除外はしない。not_found 一般はバグの兆候でもあるため、
 * 抑制はサーバーがエラーコードで「想定内」と明示した経路だけに限定する。
 */
/**
 * そのmechanismが「どこにも捕まえられずunhandledrejectionとして届いた」ことを表すか。
 *
 * SDKが付ける値はバージョンで変わる。9系は `onunhandledrejection`、11系は
 * `auto.browser.global_handlers.onunhandledrejection`（`@sentry/browser` の globalhandlers）。
 * 完全一致で書くとSDKを上げた時点で判定が外れ、落としていたノイズが再び送られ始めるため、
 * 接尾辞で判定する。デプロイ直後は古いバンドルを開いたままのタブも残るので、両方の形を拾う。
 */
const isUnhandledRejectionMechanism = (type: string | undefined): boolean =>
  !!type && type.endsWith('onunhandledrejection')

/**
 * iOS Safari / Chrome Mobile iOSで、IndexedDBの接続をブラウザが閉じた後にtransactionを開こうとして
 * 失敗するWebKit起因のエラーのうち、どこにも捕まえられずonunhandledrejectionで届いたものか。
 *
 * 実際に届いているのはFirebase Authがログイン状態の保存に使うIndexedDBのもので（IESAPURI-COMPANY-AA）、
 * stacktraceは@firebase/authの内部だけ。Firebase Authは次の操作で接続を開き直すので実害がない。
 * 同じ文言は企業画面のアップロードキュー（uploadQueue。接続をblocking()で閉じる）でも出うるが、
 * そちらはcaptureExceptionで明示的に送る（mechanism.handled=true）ので、ここでは落とさない。
 * ignoreErrorsは文言の部分一致で両方を落としてしまうため、届き方まで見てbeforeSendで判定する。
 * ブラウザ側のstacktraceはminifyされたファイル名なので、@firebase/authかどうかは名前では判定できない。
 */
const isUnhandledIdbConnectionClosing = (event: ErrorEvent): boolean =>
  (event.exception?.values ?? []).some(
    value =>
      (value.value ?? '').includes('The database connection is closing') &&
      isUnhandledRejectionMechanism(value.mechanism?.type),
  )

export const createSentryBeforeSend =
  (): ((event: ErrorEvent, hint: EventHint) => ErrorEvent | null) => (event, hint) => {
    if (isUnhandledIdbConnectionClosing(event)) return null

    const error = hint.originalException
    if (!(error instanceof ConnectError)) return event

    if (error.code === Code.InvalidArgument) return null

    const { code } = parseStructuredError(error)
    if (code && SENTRY_EXPECTED_APP_ERROR_CODES.includes(code)) return null

    return event
  }

/**
 * `Sentry.init` に渡す、アプリ共通のオプション一式。
 *
 * 送信データの範囲（`dataCollection`）と送信前フィルタ（`ignoreErrors` / `denyUrls` / `beforeSend`）は、
 * company / admin / customer で同じでなければならない。個別に渡す形だと、**書き忘れても型・lint・テストの
 * どれも落ちない**まま、そのアプリだけ SDK の既定（v11 は IP アドレス・Cookie・本文を収集する側）に
 * 戻ってしまう。1 つの関数から展開する形にして、書き忘れが起こらないようにする。
 *
 * 各 init はこれを展開したうえで、アプリごとに違うもの（DSN・環境・トレースの伝播先・サンプリング）
 * だけを書く。
 */
export const createSentryBaseOptions = () => ({
  ignoreErrors: SENTRY_IGNORE_ERRORS,
  denyUrls: SENTRY_DENY_URLS,
  dataCollection: SENTRY_DATA_COLLECTION,
  // 想定内のユーザー導線で必ず出るエラー（入力バリデーション / 無効な設定リンク等）は
  // アプリのバグではないため送信しない
  beforeSend: createSentryBeforeSend(),
})
